/**
 * MOSS Template Runtime
 *
 * Provides utility functions for migrated MOSS templates.
 * All functions are pure (no side effects) and synchronous.
 *
 * NOTE: This file is bundled to browser-runtime.js via tsup.
 * Run `npm run moss:build-runtime` to rebuild after changes.
 */

import { parseDisplayDate } from '../../../utils/parseDisplayDate';

// ============================================================================
// Context Types
// ============================================================================

export interface TemplateContext {
  /** Certificate of Analysis data */
  coa: COAData;
  /** Product metadata */
  product: ProductData;
  /** User-provided theme overrides from form */
  theme: Record<string, any>;
  /** Print job data */
  job: JobData;
  /** Global metadata (ingredient labels, terpene names) */
  metrc: MetrcGlobals;
}

export interface COAData {
  id?: string;
  strainName?: string;
  productName?: string;
  batch?: string;
  packageLotNumber?: string;
  origin?: string;
  productionBatchDate?: string | null;
  packagedDate?: string | Date;
  expirationDate?: string | Date;
  useByDate?: string | Date;
  sellByDate?: string | Date;
  harvestDate?: string | Date;
  dateTested?: string | Date;
  manufacturedDate?: string | Date;
  sourcePackage?: string;
  licenseNumber?: string;
  category?: string;
  manufacturer?: {
    name?: string;
    licenseNumber?: string;
  };
  lab?: {
    name?: string;
    licenseNumber?: string;
  };
  unit?: {
    weight?: number;
  };
  ingredients?: Record<string, IngredientData>;
  terpenes?: Record<string, TerpeneData>;
}

export interface IngredientData {
  percent?: number;
  weight?: {
    amount?: number;
    srv?: number;
    perUnit?: number;
    pkg?: number;
    ml?: number;
    unit?: string;
  };
}

export interface TerpeneData {
  percent?: number;
  label?: string;
}

export interface ProductData {
  strain?: string;
  subSpecies?: string;
  title?: string;
  barcode?: {
    value?: string;
  };
}

export interface JobData {
  packageTag?: string;
  packagedDate?: string | Date;
  quantity?: number;
}

export interface MetrcGlobals {
  ingredients: Record<string, { label: string }>;
  cannabinoids: Record<string, { label: string }>;
  terpenes: Record<string, { label: string; noGreekLabel?: string }>;
}

// ============================================================================
// Number Functions
// ============================================================================

/**
 * Convert value to number with optional decimal formatting.
 * Equivalent to MOSS `<number:` function.
 */
export function toNumber(value: any, maxDecimal?: number): number {
  if (value === undefined || value === null || value === '') {
    return 0;
  }

  let num: number;
  if (typeof value === 'string') {
    num = parseFloat(value);
  } else if (typeof value === 'number') {
    num = value;
  } else {
    num = 0;
  }

  if (isNaN(num)) {
    return 0;
  }

  if (maxDecimal !== undefined) {
    // Round to specified decimals
    const factor = Math.pow(10, maxDecimal);
    num = Math.round(num * factor) / factor;
  }

  return num;
}

/**
 * Format number to fixed decimal string.
 * Equivalent to MOSS `<toFixed:` function.
 */
export function toFixed(value: any, fractionDigits: number = 2): string {
  const num = toNumber(value);
  return num.toFixed(fractionDigits);
}

// ============================================================================
// String Functions
// ============================================================================

/**
 * Convert value to string.
 * Equivalent to MOSS `<string:` function.
 */
export function toString(value: any): string {
  if (value === undefined || value === null) {
    return '';
  }
  return String(value);
}

/**
 * Uppercase string.
 * Equivalent to MOSS `<capslock:` function.
 */
export function capslock(value: any): string {
  return toString(value).toUpperCase();
}

/**
 * Lowercase string.
 * Equivalent to MOSS `<lowercase:` function.
 */
export function lowercase(value: any): string {
  return toString(value).toLowerCase();
}

/**
 * Capitalize each word.
 * Equivalent to MOSS `<capitalize:` function.
 */
export function capitalize(value: any): string {
  return toString(value).replace(/\w\S*/g, txt =>
    txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
  );
}

/**
 * Proper title case (respects minor words).
 * Equivalent to MOSS `<proper:` function.
 */
export function proper(value: any): string {
  let str = toString(value);
  if (!str) return '';

  str = str.replace(/([^\W_]+[^\s-]*) */g, txt =>
    txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
  );

  const lowers = [
    'A', 'An', 'The', 'And', 'But', 'Or', 'For', 'Nor', 'As', 'At',
    'By', 'For', 'From', 'In', 'Into', 'Near', 'Of', 'On', 'Onto',
    'To', 'With', 'Upon', 'Van'
  ];

  for (const word of lowers) {
    str = str.replace(new RegExp(`\\s${word}\\s`, 'g'), txt => txt.toLowerCase());
  }

  return str;
}

// ============================================================================
// Date Functions
// ============================================================================

/**
 * Parse a date value into a Date object.
 */
function parseDate(value: any): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Add or subtract time from a date.
 */
function adjustDate(date: Date, adjustment: string, subtract: boolean = false): Date {
  const parts = adjustment.trim().split(/\s+/);
  if (parts.length < 2) return date;

  const amount = parseInt(parts[0]) * (subtract ? -1 : 1);
  const unit = parts[1].toLowerCase();

  const result = new Date(date);
  switch (unit) {
    case 'day':
    case 'days':
      result.setDate(result.getDate() + amount);
      break;
    case 'month':
    case 'months':
      result.setMonth(result.getMonth() + amount);
      break;
    case 'year':
    case 'years':
      result.setFullYear(result.getFullYear() + amount);
      break;
    case 'hour':
    case 'hours':
      result.setHours(result.getHours() + amount);
      break;
    case 'minute':
    case 'minutes':
      result.setMinutes(result.getMinutes() + amount);
      break;
    case 'second':
    case 'seconds':
      result.setSeconds(result.getSeconds() + amount);
      break;
    case 'week':
    case 'weeks':
      result.setDate(result.getDate() + (amount * 7));
      break;
  }
  return result;
}

/**
 * Format a date using a format string.
 * Supports common format tokens: YYYY, YY, MM, M, DD, D, HH, H, mm, ss
 */
function formatDateString(date: Date, format: string): string {
  const pad = (n: number): string => n.toString().padStart(2, '0');

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  const replacements: Record<string, string> = {
    YYYY: year.toString(),
    YY: year.toString().slice(-2),
    MM: pad(month),
    M: month.toString(),
    DD: pad(day),
    D: day.toString(),
    HH: pad(hours),
    H: hours.toString(),
    mm: pad(minutes),
    ss: pad(seconds),
  };

  return format.replace(/YYYY|YY|MM|M|DD|D|HH|H|mm|ss/g, (token) => replacements[token]);
}

/**
 * Parse and format date.
 * Equivalent to MOSS `<date:` function.
 */
export function formatDate(
  value: any,
  format: string = 'MM/DD/YYYY',
  options?: { add?: string; subtract?: string }
): string {
  if (!value) return '';

  let d = parseDisplayDate(value);
  if (!d) return '';

  if (options?.add) {
    d = adjustDate(d, options.add, false);
  }

  if (options?.subtract) {
    d = adjustDate(d, options.subtract, true);
  }

  return formatDateString(d, format);
}

/**
 * Get relative time string (e.g., "2 days ago", "in 3 hours").
 */
export function fromNow(value: any): string {
  if (!value) return '';
  const d = parseDate(value);
  if (!d) return '';

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.abs(Math.floor(diffMs / 1000));
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  const past = diffMs > 0;
  const suffix = past ? ' ago' : '';
  const prefix = past ? '' : 'in ';

  if (diffYears > 0) return `${prefix}${diffYears} year${diffYears > 1 ? 's' : ''}${suffix}`;
  if (diffMonths > 0) return `${prefix}${diffMonths} month${diffMonths > 1 ? 's' : ''}${suffix}`;
  if (diffDays > 0) return `${prefix}${diffDays} day${diffDays > 1 ? 's' : ''}${suffix}`;
  if (diffHours > 0) return `${prefix}${diffHours} hour${diffHours > 1 ? 's' : ''}${suffix}`;
  if (diffMin > 0) return `${prefix}${diffMin} minute${diffMin > 1 ? 's' : ''}${suffix}`;
  return 'just now';
}

// ============================================================================
// Collection Functions
// ============================================================================

/**
 * Get value at a dot-notation keypath.
 * Matches valueForKeyPath from typed-json-transform.
 */
function valueForKeyPath(keypath: string, obj: any): any {
  if (!keypath || !obj) return obj;
  const parts = keypath.split('.');
  let result = obj;
  for (const part of parts) {
    result = result?.[part];
  }
  return result;
}

interface SortArgs {
  from: any[] | Record<string, any>;
  by?: string;
  key?: string;
  descending?: boolean;
}

/**
 * Sort array or object values.
 * Equivalent to MOSS `<sort:` function.
 * Matches original implementation from packages/lib/moss/containers.ts
 *
 * Supports two call styles:
 * - MOSS style: sort({from: [...], by: 'keypath', descending: true})
 * - Direct style: sort(items, 'keypath', descending) - for migrated templates
 *
 * Default keypath is 'key' (important for sorting map results with {key, value} pairs)
 */
export function sort(
  argsOrItems: SortArgs | any[] | Record<string, any>,
  byArg?: string,
  descendingArg?: boolean
): any[] | Record<string, any> {
  let from: any[] | Record<string, any>;
  let keypath: string;
  let descending: boolean;

  // Check if called with MOSS-style object args or direct args
  if (argsOrItems && typeof argsOrItems === 'object' && 'from' in argsOrItems && !Array.isArray(argsOrItems)) {
    // MOSS style: sort({from: [...], by: 'key', descending: true})
    const args = argsOrItems as SortArgs;
    from = args.from;
    keypath = args.by || args.key || 'key';
    descending = args.descending ?? false;
  } else {
    // Direct style: sort(items, 'keypath', descending)
    from = argsOrItems as any[] | Record<string, any>;
    keypath = byArg || 'key';
    descending = descendingArg ?? false;
  }

  const sortCmp = (a: any, b: any): number => {
    const ca = valueForKeyPath(keypath, a);
    const cb = valueForKeyPath(keypath, b);
    return ca === cb
      ? 0
      : ca > cb
        ? descending ? -1 : 1
        : descending ? 1 : -1;
  };

  if (from) {
    if (Array.isArray(from)) {
      // Sort array (copy to be safe)
      return [...from].sort(sortCmp);
    } else {
      // Object: convert to array with keys, sort, then back to object
      const keys = Object.keys(from);
      const keymapped = keys.map((key) => ({ ...from[key], key }));
      const sortedObjects = keymapped.sort(sortCmp);
      const ret: Record<string, any> = {};
      for (const o of sortedObjects) {
        const { key } = o;
        ret[key] = from[key];
      }
      return ret;
    }
  }
  return from;
}

interface SliceArgs<T> {
  from: T[] | string | Record<string, T>;
  start?: number;
  end?: number;
}

/**
 * Slice array, string, or object.
 * Equivalent to MOSS `<slice:` function.
 * Matches original implementation from packages/lib/moss/containers.ts
 *
 * Can be called as:
 * - slice({from: [...], start: 0, end: 5})
 * - slice(items, start, end) for simple usage
 */
export function slice<T>(
  args: SliceArgs<T> | T[] | string | Record<string, T>,
  start?: number,
  end?: number
): T[] | string | Record<string, T> {
  let from: T[] | string | Record<string, T>;
  let sliceStart: number | undefined;
  let sliceEnd: number | undefined;

  if (args && typeof args === 'object' && !Array.isArray(args) && 'from' in args) {
    // MOSS style: {from: [...], start: 0, end: 5}
    const sliceArgs = args as SliceArgs<T>;
    from = sliceArgs.from;
    sliceStart = sliceArgs.start;
    sliceEnd = sliceArgs.end;
  } else {
    // Old API: slice(items, start, end)
    from = args as T[] | string | Record<string, T>;
    sliceStart = start;
    sliceEnd = end;
  }

  if (!from) return [];

  if (Array.isArray(from) || typeof from === 'string') {
    if (sliceStart !== undefined) return from.slice(sliceStart, sliceEnd);
    return from.slice(sliceEnd);
  } else {
    // Object: slice keys
    let keys = Object.keys(from);
    if (sliceStart !== undefined) keys = keys.slice(sliceStart, sliceEnd);
    else keys = keys.slice(sliceEnd);
    const ret: Record<string, T> = {};
    for (const k of keys) {
      ret[k] = (from as Record<string, T>)[k];
    }
    return ret;
  }
}

/**
 * Join array or object values to string.
 * Equivalent to MOSS `<join:` function.
 * Matches original implementation from packages/lib/moss/containers.ts
 *
 * Can be called as:
 * - join([...]) -> items.join(', ')
 * - join({from: [...], separator: ', '}) -> from.join(separator)
 * - join({from: {...}, separator: ', '}) -> Object.values(from).join(separator)
 */
export function join(
  args: any[] | { from: any[] | Record<string, any>; separator?: string; with?: string }
): string {
  // If it's already an array, just join with default separator
  if (Array.isArray(args)) {
    return args.join(', ');
  }

  // MOSS style: {from: [...], separator: ', ', with: ', '}
  const { from, separator, with: alt_separator } = args || {};
  const sep = separator ?? alt_separator ?? ', ';

  if (!from) return '';

  if (Array.isArray(from)) {
    return from.join(sep);
  } else {
    // Object: join values
    const keys = Object.keys(from);
    const ret: any[] = [];
    for (const k of keys) {
      ret.push(from[k]);
    }
    return ret.join(sep);
  }
}

/**
 * Convert object to array with keys preserved.
 * Useful for iterating over MOSS objects that become JS Records.
 */
export function toArray<T>(obj: Record<string, T>): Array<T & { _key: string }> {
  return Object.entries(obj ?? {}).map(([key, value]) => ({
    ...(value as any),
    _key: key,
  }));
}

/**
 * Filter object by excluding keys.
 * Equivalent to MOSS minus operator (-:).
 */
export function omit<T>(obj: Record<string, T>, keys: string[]): Record<string, T> {
  const result: Record<string, T> = {};
  for (const [k, v] of Object.entries(obj ?? {})) {
    if (!keys.includes(k)) {
      result[k] = v;
    }
  }
  return result;
}

/**
 * Filter object by including only specified keys.
 */
export function pick<T>(obj: Record<string, T>, keys: string[]): Record<string, T> {
  const result: Record<string, T> = {};
  for (const k of keys) {
    if (k in (obj ?? {})) {
      result[k] = obj[k];
    }
  }
  return result;
}

// ============================================================================
// Match/Conditional Functions
// ============================================================================

/**
 * Match value against cases.
 * Equivalent to MOSS `<match:` function.
 */
export function match<T>(
  value: any,
  cases: Record<string, T>,
  defaultValue?: T
): T | undefined {
  if (value in cases) {
    return cases[value];
  }
  return defaultValue ?? cases['default'];
}

// ============================================================================
// Cannabinoid/Terpene Helpers
// ============================================================================

export interface CannabinoidValue {
  percent: number;
  total: number;
  serving: number;
  package: number;
}

/**
 * Extract cannabinoid values with theme override support.
 * Common pattern in MOSS templates.
 */
export function getCannabinoid(
  ctx: TemplateContext,
  name: string,
  maxDecimal: number = 2
): CannabinoidValue {
  const ingredient = ctx.coa?.ingredients?.[name];
  const theme = ctx.theme ?? {};

  return {
    percent: toNumber(theme[`${name}percent`] ?? ingredient?.percent, maxDecimal),
    total: toNumber(theme[`${name}total`] ?? ingredient?.weight?.amount, maxDecimal),
    serving: toNumber(theme[`${name}serving`] ?? ingredient?.weight?.srv, maxDecimal),
    package: toNumber(theme[`${name}package`] ?? ingredient?.weight?.pkg, maxDecimal),
  };
}

/**
 * Get top N terpenes sorted by percent.
 */
export function getTopTerpenes(
  ctx: TemplateContext,
  n: number = 3,
  excludeKeys: string[] = ['totalTerpenes', 'otherTerpenes', 'humulene']
): Array<{ name: string; percent: number; label: string }> {
  const terpenes = ctx.coa?.terpenes ?? {};

  return Object.entries(terpenes)
    .filter(([key]) => !excludeKeys.includes(key))
    .map(([key, value]) => ({
      name: key,
      percent: value.percent ?? 0,
      label: ctx.metrc?.terpenes?.[key]?.label ?? key,
    }))
    .sort((a, b) => b.percent - a.percent)
    .slice(0, n);
}

// ============================================================================
// Theme Schema Types
// ============================================================================

export interface ThemeFieldDef {
  label: string;
  kind?: 'text' | 'number' | 'date' | 'select' | 'select1' | 'checkbox';
  options?: Record<string, string>;
}

export interface ThemeSchema {
  label: string;
  fields: Record<string, ThemeFieldDef>;
  defaults: Record<string, any>;
}

// ============================================================================
// Metrc Globals Management
// ============================================================================

let _metrcGlobals: MetrcGlobals = {
  ingredients: {},
  cannabinoids: {},
  terpenes: {},
};

/**
 * Set the global metrc data (ingredient labels, terpene names, etc.)
 */
export function setMetrcGlobals(globals: MetrcGlobals): void {
  _metrcGlobals = globals;
}

/**
 * Get the current metrc globals.
 */
export function getMetrcGlobals(): MetrcGlobals {
  return _metrcGlobals;
}

/**
 * Get the label for a terpene by key.
 */
export function getTerpeneLabel(key: string): string {
  return _metrcGlobals?.terpenes?.[key]?.label ?? key;
}

/**
 * Get the label for an ingredient by key.
 */
export function getIngredientLabel(key: string): string {
  return _metrcGlobals?.ingredients?.[key]?.label ??
         _metrcGlobals?.cannabinoids?.[key]?.label ??
         key;
}

/**
 * Select and merge values based on selector flags.
 * Used for conditional template rendering.
 */
export function selectMerge(
  selectors: Record<string, boolean>,
  cases: Record<string, any>,
  defaultValue?: any
): any {
  for (const [key, isSelected] of Object.entries(selectors)) {
    if (isSelected && key in cases) {
      return cases[key];
    }
  }
  return defaultValue ?? cases['default'];
}

/**
 * Parse selector string into object.
 * E.g., "=flag1,flag2" -> { flag1: true, flag2: true }
 */
export function parseSelectors(selectorStr: string): Record<string, boolean> {
  if (!selectorStr) return {};
  const result: Record<string, boolean> = {};
  const parts = selectorStr.replace(/^=/, '').split(',');
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed) {
      result[trimmed] = true;
    }
  }
  return result;
}

/**
 * Return first non-empty value (ifEmpty replacement).
 */
export function ifEmpty(value: any, fallback: any): any {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  return value;
}

/**
 * Check if a value is numeric (matches typed-json-transform isNumeric).
 * Returns true for numbers and numeric strings like "0", "1", etc.
 */
function isNumeric(n: any): boolean {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

/**
 * Check if a value is an Object (not array, not date, not null).
 * Matches typed-json-transform check(val, Object).
 */
function isObject(val: any): boolean {
  return val !== null && typeof val === 'object' && !(val instanceof Date) && !Array.isArray(val);
}

/**
 * Check if a value is a {key, value} pair that should be unwrapped.
 * This matches the okmap behavior from typed-json-transform.
 */
function isKeyValuePair(v: any): v is { key: string | number; value: any } {
  if (!isObject(v)) return false;
  const keys = Object.keys(v);
  return keys.length === 2 && (keys[0] === 'key' || keys[1] === 'key');
}

/**
 * Map function for arrays and objects.
 * Matches the okmap behavior from typed-json-transform exactly:
 * - If mapper returns { key, value }, extracts value and uses key as the new key
 * - Otherwise keeps original key and mapper's return value
 * - Returns array if all resulting keys are numeric, otherwise object
 * - Populates array at index k if k >= 0 (works with numeric strings)
 *
 * For arrays: mapper receives (item, key, index) where key === index
 * For objects: mapper receives (item, key, index) where key is the object key
 */
export function map<T, R>(
  items: T[] | Record<string, T>,
  mapper: (item: T, key: string | number, index: number) => R
): R[] | Record<string, R> {
  const obj: Record<string, R> = {};
  const arr: R[] = [];

  // Use same iteration pattern as typed-json-transform's each()
  if (Array.isArray(items)) {
    let index = 0;
    for (const v of items) {
      let k: string | number = index;
      // Pass (item, key, index) - for arrays, key === index
      let result = mapper(v, k, index);

      // okmap behavior: unwrap { key, value } pairs
      if (isKeyValuePair(result)) {
        k = result.key;
        result = result.value as R;
      }

      obj[k as string] = result;
      // Original: if (k >= 0) - works with string "0" due to JS coercion
      if ((k as any) >= 0) {
        arr[k as number] = result;
      }
      index++;
    }
  } else {
    let index = 0;
    for (const originalKey of Object.keys(items ?? {})) {
      let k: string | number = originalKey;
      // Pass (item, key, index) - for objects, key is the object key, index is iteration order
      let result = mapper(items[originalKey], originalKey, index);

      // okmap behavior: unwrap { key, value } pairs
      if (isKeyValuePair(result)) {
        k = result.key;
        result = result.value as R;
      }

      obj[k as string] = result;
      // Original: if (k >= 0) - works with string "0" due to JS coercion
      if ((k as any) >= 0) {
        arr[k as number] = result;
      }
      index++;
    }
  }

  // Original: if (every(Object.keys(o), (k) => check(k, Number))) return a
  // check(k, Number) uses isNumeric which works on strings
  if (Object.keys(obj).every(k => isNumeric(k))) {
    return arr;
  }
  return obj;
}

/**
 * Filter function for arrays and objects.
 */
export function filter<T>(
  items: T[] | Record<string, T>,
  predicate: (item: T, index: number | string) => boolean
): T[] | Record<string, T> {
  if (Array.isArray(items)) {
    return items.filter((item, idx) => predicate(item, idx));
  }

  const result: Record<string, T> = {};
  for (const [key, value] of Object.entries(items ?? {})) {
    if (predicate(value, key)) {
      result[key] = value;
    }
  }
  return result;
}

// ============================================================================
// SVG Text Wrapping Functions (ptspan, ctspan, cdata, wrap)
// ============================================================================

/**
 * Wrap text in CDATA for safe SVG embedding.
 * Equivalent to MOSS `<cdata:` function.
 */
export function cdata(args: any): string {
  let from: any;

  if (args === undefined || args === null) return '';

  if (typeof args === 'object' && 'from' in args) {
    from = args.from;
  } else {
    from = args;
  }

  if (from === undefined || from === null) return '';

  let content: string;
  switch (typeof from) {
    case 'object':
      content = JSON.stringify(from);
      break;
    case 'function':
      content = from();
      break;
    default:
      content = String(from);
      break;
  }

  return `<![CDATA[${content}]]>`;
}

interface TextSpanOptions {
  /** The text to wrap */
  text: string;
  /** Maximum characters per line */
  width: number;
  /** Vertical offset between lines (e.g., "1em", 7, "7px") */
  dy?: number | string;
  /** Horizontal offset for each line */
  dx?: number | string;
  /** Whether to wrap content in CDATA (default true) */
  cdataWrap?: boolean;
}

interface WrapOptions extends TextSpanOptions {
  /** Number of lines to reserve (for vertical alignment) */
  numberOfLines?: number;
  /** Vertical alignment: "top" (default) or "bottom" */
  verticalAlign?: 'top' | 'bottom';
}

/**
 * Format dy/dx value to string with units.
 */
function formatUnit(value: number | string | undefined, defaultValue: string): string {
  if (value === undefined || value === null || value === '') return defaultValue;
  if (typeof value === 'string') return value;
  return `${value}em`;
}

/**
 * Paragraph text span - breaks text on word boundaries.
 * Equivalent to MOSS `<ptspan:` function.
 *
 * Takes text and wraps it into multiple <tspan> elements,
 * breaking on whitespace to fit within specified character width.
 */
export function ptspan(options: TextSpanOptions): string[] {
  const { text, width, dy, dx, cdataWrap = true } = options;

  if (!text) return [];

  let endOfLastSlice = 0;
  let ndx = 0;
  const result: string[] = [];

  do {
    ndx = ndx + width;
    if (ndx < text.length) {
      // Find last space before the width limit
      do {
        ndx = ndx - 1;
        if (ndx < 0) {
          // No space found, return original text
          return [text];
        }
      } while (text[ndx] !== ' ');
      result.push(text.slice(endOfLastSlice, ndx));
      endOfLastSlice = ndx;
    } else {
      result.push(text.slice(endOfLastSlice, text.length));
      break;
    }
  } while (ndx < text.length);

  const y = formatUnit(dy, '1em');
  const x = formatUnit(dx, '0');

  return result.map((r) => {
    const c = cdataWrap ? cdata(r) : r;
    return `<tspan x="0em" dy="${y}" dx="${x}">${c}</tspan>`;
  });
}

/**
 * Character text span - breaks text at exact character positions.
 * Equivalent to MOSS `<ctspan:` function.
 *
 * Unlike ptspan, this breaks at exact character counts regardless of word boundaries.
 */
export function ctspan(options: TextSpanOptions): string[] {
  const { text, width, dy, dx, cdataWrap = true } = options;

  if (!text) return [];

  const results: string[][] = [[]];
  let ndx = 0;

  do {
    const majorIndex = Math.floor(ndx / width);
    const minorIndex = ndx % width;
    if (results[majorIndex] === undefined) {
      results[majorIndex] = [];
    }
    results[majorIndex][minorIndex] = text[ndx];
    ndx = ndx + 1;
  } while (ndx < text.length);

  const y = formatUnit(dy, '1em');
  const x = formatUnit(dx, '0');

  return results.map((r) => {
    const c = cdataWrap ? cdata(r.join('')) : r.join('');
    return `<tspan x="0em" dy="${y}" dx="${x}">${c}</tspan>`;
  });
}

/**
 * Apply vertical alignment to tspan array.
 * Prepends invisible tspans to push content down for bottom alignment.
 */
function verticalAlignTspans(
  tspans: string[],
  options: { dy?: number | string; dx?: number | string; numberOfLines: number; verticalAlign?: string }
): string[] {
  const { dy, dx, numberOfLines, verticalAlign } = options;

  if (verticalAlign === 'bottom' && tspans.length < numberOfLines) {
    const y = formatUnit(dy, '1em');
    const x = formatUnit(dx, '0');

    while (tspans.length < numberOfLines) {
      tspans.unshift(`<tspan x="0em" dy="${y}" dx="${x}" visibility="hidden">''</tspan>`);
    }
  }

  return tspans;
}

/**
 * Combined text wrapping function.
 * Tries ptspan first (word breaks), falls back to ctspan (character breaks) if single line.
 * Equivalent to MOSS `<wrap:` function.
 *
 * @example
 * wrap({ text: "Hello World", width: 5, dy: "1em" })
 * // If text can break on words, uses ptspan
 * // Otherwise uses ctspan for character-level breaking
 */
export function wrap(options: WrapOptions): string {
  const { numberOfLines = 0, verticalAlign: vAlign } = options;

  // Try paragraph-style wrapping first
  const pt = ptspan(options);

  if (pt.length > 1) {
    // Multiple lines from word breaking
    if (numberOfLines > 0) {
      return verticalAlignTspans(pt, { ...options, numberOfLines, verticalAlign: vAlign }).join('');
    }
    return pt.join('');
  }

  // Fall back to character-level breaking
  const ct = ctspan(options);

  if (numberOfLines > 0) {
    return verticalAlignTspans(ct, { ...options, numberOfLines, verticalAlign: vAlign }).join('');
  }
  return ct.join('');
}

// ============================================================================
// QR Code / Barcode Functions (Layer 3 - Runtime Resolution)
// ============================================================================

/**
 * QR Code options interface.
 * These options are passed to the QR code generator at render time.
 */
export interface QROptions {
  /** The content/URL to encode */
  content: string;
  /** Width in pixels */
  width?: number;
  /** Height in pixels */
  height?: number;
  /** Margin around QR code */
  margin?: number;
  /** Dot styling options */
  dotsOptions?: {
    type?: 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'square' | 'extra-rounded';
    color?: string;
  };
  /** Background options */
  backgroundOptions?: {
    color?: string;
  };
  /** Corner square styling */
  cornersSquareOptions?: {
    type?: 'square' | 'extra-rounded' | 'dot';
    color?: string;
  };
  /** Corner dot styling */
  cornersDotOptions?: {
    type?: 'square' | 'dot';
    color?: string;
  };
  /** QR error correction level */
  qrOptions?: {
    errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  };
  /** Image options for center logo */
  imageOptions?: {
    imageSize?: number;
    margin?: number;
    hideShape?: 'radial' | 'square';
    crossOrigin?: string;
  };
  /** Base64 encoded image for center logo */
  image?: string;
  /** Base64 encoded image data */
  image64?: string;
  /** Output type */
  type?: 'svg' | 'canvas';
}

/**
 * Barcode options interface.
 */
export interface BarcodeOptions {
  /** The barcode value to encode */
  barcode: string | number;
  /** Barcode format (default: code128) */
  format?: 'code128' | 'upc' | 'ean13' | 'ean8' | 'code39';
  /** Line color */
  lineColor?: string;
  /** Background color */
  background?: string;
  /** Height in pixels */
  height?: number;
  /** Bar width multiplier */
  width?: number;
  /** Margin */
  margin?: number;
  /** Whether to display value text */
  displayValue?: boolean;
}

/**
 * Generate a QR code placeholder.
 *
 * In the migration architecture, QR codes are generated at Layer 3 (runtime).
 * This function returns a placeholder that will be resolved when the label is rendered.
 *
 * For the browser runtime, this can be replaced with actual QR generation using
 * a library like qr-code-styling or similar.
 *
 * @example
 * // In template:
 * qrContent: qr({ content: "https://example.com", width: 100 })
 *
 * // Returns placeholder SVG during migration/testing
 * // Actual QR generated at render time
 */
export function qr(options: QROptions): string {
  const { content, width = 100, height } = options;
  const h = height ?? width;

  // Return a placeholder SVG that indicates QR content
  // In production, this would be replaced with actual QR generation
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${h}" viewBox="0 0 ${width} ${h}">
    <rect width="${width}" height="${h}" fill="#f0f0f0" stroke="#ccc"/>
    <text x="${width / 2}" y="${h / 2}" text-anchor="middle" dominant-baseline="middle" font-size="8" fill="#666">QR: ${content?.slice(0, 20) ?? 'N/A'}...</text>
  </svg>`;
}

/**
 * Alias for qr function.
 */
export const qrCodeToSvg = qr;

/**
 * Modern QR code generator (placeholder).
 * Same as qr() but typically used with more styling options.
 */
export function mqr(options: QROptions): string {
  return qr(options);
}

/**
 * Generate a barcode placeholder.
 *
 * In the migration architecture, barcodes are generated at Layer 3 (runtime).
 * This function returns a placeholder that will be resolved when the label is rendered.
 *
 * For the browser runtime, this can be replaced with actual barcode generation using
 * a library like jsbarcode or similar.
 *
 * @example
 * // In template:
 * barcodeContent: barcode({ barcode: "123456789012", format: "upc" })
 */
export function barcode(options: BarcodeOptions): string {
  const { barcode: value, format = 'code128', height = 50, width: barWidth = 2 } = options;
  const w = Math.max(100, String(value).length * 10 * barWidth);

  // Return a placeholder SVG that indicates barcode content
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${height}" viewBox="0 0 ${w} ${height}">
    <rect width="${w}" height="${height - 15}" fill="#f0f0f0" stroke="#ccc"/>
    <text x="${w / 2}" y="${height - 5}" text-anchor="middle" font-size="8" fill="#000">${value}</text>
    <text x="${w / 2}" y="${(height - 15) / 2}" text-anchor="middle" dominant-baseline="middle" font-size="6" fill="#999">[${format}]</text>
  </svg>`;
}

/**
 * Alias for barcode function.
 */
export const barcodeToSvg = barcode;

/**
 * Format UPC string with spacing.
 * Used for displaying UPC barcodes with standard visual formatting.
 *
 * @example
 * upcString("123456789012") // "1    23456    78901    2"
 */
export function upcString(s: string | undefined): string {
  if (s?.length === 12) {
    const a = s.slice(0, 1);
    const b = s.slice(1, 6);
    const c = s.slice(6, 11);
    const d = s.slice(11, 12);
    return `${a}    ${b}    ${c}    ${d}`;
  }
  return s ?? '';
}

// ============================================================================
// Metrc Globals Data Export
// ============================================================================

export { metrcGlobalsData, cannabinoidsMeta, terpenesMeta, ingredientsMeta, cannabinoidKeys, terpeneKeys } from './metrc-globals';
export type { IngredientMeta } from './metrc-globals';
